/* eslint-disable react/prop-types */
import React from "react";
import {
  Box,
  Typography,
  TextField,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import Select from "react-select";
import makeAnimated from "react-select/animated";
import { useTheme } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import { getMultiSelectStyles } from "@/lib/styles";

const animatedComponents = makeAnimated();

const StepBasicInfo = ({
  nodeData,
  setNodeData,
  userGroupIds,
  userGroupsList,
  handleMultiChange,
  translationPrefix = "newWorkFlow",
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const multiSelectStyles = getMultiSelectStyles(theme);

  // Internal state to track if fields have been touched
  const [touched, setTouched] = React.useState({
    userGroups: false,
    actionName: false,
    status: false,
  });

  // Validation state
  const isUserGroupValid = userGroupIds.length > 0;
  const isActionNameValid = Boolean(nodeData.action_name);
  const isStatusValid = Boolean(nodeData.status);

  return (
    <Box
      sx={{
        p: 3,
        mb: 3,
        bgcolor: theme.palette.mode === "dark" ? "grey.900" : "grey.50",
        borderRadius: 2,
        border: 1,
        borderColor: "divider",
      }}
    >
      <Typography
        variant="h6"
        color="primary"
        gutterBottom
        sx={{
          fontWeight: "bold",
          mb: 3,
          display: "flex",
          alignItems: "center",
          gap: 1,
        }}
      >
        {t(`${translationPrefix}.basicInformationTitle`) ||
          t(`${translationPrefix}.basicInfoTitle`) ||
          "Basic Information"}
      </Typography>

      <Grid direction="column" spacing={3} container>
        <Grid container spacing={3}>
          <Grid size={6}>
            <Typography
              variant="subtitle1"
              gutterBottom
              sx={{ fontWeight: "medium", mb: 1 }}
            >
              {t(`${translationPrefix}.actionNameLabel`)} *
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {t(`${translationPrefix}.actionNameDescription`)}
            </Typography>
            <TextField
              value={nodeData.action_name || ""}
              onChange={(e) =>
                setNodeData({ ...nodeData, action_name: e.target.value })
              }
              onBlur={() =>
                setTouched((prev) => ({ ...prev, actionName: true }))
              }
              fullWidth
              variant="outlined"
              placeholder={t(`${translationPrefix}.actionNamePlaceholder`)}
              error={touched.actionName && !isActionNameValid}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 1,
                  minHeight: 48,
                  background: theme.palette.background.paper,
                },
              }}
              aria-label={t(`${translationPrefix}.actionNameAria`)}
            />
            {touched.actionName && !isActionNameValid && (
              <Typography
                color="error"
                variant="body2"
                sx={{
                  mt: 1,
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                ⚠️ {t(`${translationPrefix}.errorNoActionName`)}
              </Typography>
            )}
          </Grid>

          <Grid size={6}>
            <Typography
              variant="subtitle1"
              gutterBottom
              sx={{ fontWeight: "medium", mb: 1 }}
            >
              {t(`${translationPrefix}.statusLabel`)} *
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {t(`${translationPrefix}.statusDescription`)}
            </Typography>
            <TextField
              value={nodeData.status || ""}
              onChange={(e) =>
                setNodeData({ ...nodeData, status: e.target.value })
              }
              onBlur={() => setTouched((prev) => ({ ...prev, status: true }))}
              fullWidth
              variant="outlined"
              placeholder={t(`${translationPrefix}.statusPlaceholder`)}
              error={touched.status && !isStatusValid}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 1,
                  minHeight: 48,
                  background: theme.palette.background.paper,
                },
              }}
              aria-label={t(`${translationPrefix}.statusAria`)}
            />
            {touched.status && !isStatusValid && (
              <Typography
                color="error"
                variant="body2"
                sx={{
                  mt: 1,
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                ⚠️ {t(`${translationPrefix}.errorNoStatus`)}
              </Typography>
            )}
          </Grid>
        </Grid>

        <Grid size={12}>
          <Typography
            variant="subtitle1"
            gutterBottom
            sx={{ fontWeight: "medium", mb: 1 }}
          >
            {t(`${translationPrefix}.authorisedUserGroupsLabel`)} *
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t(`${translationPrefix}.userGroupsDescription`) ||
              t(`${translationPrefix}.authorisedUserGroupsDescription`)}
          </Typography>
          <Select
            options={userGroupsList}
            components={animatedComponents}
            isMulti
            closeMenuOnSelect={false}
            value={userGroupIds}
            onChange={handleMultiChange("user_groups")}
            onBlur={() => setTouched((prev) => ({ ...prev, userGroups: true }))}
            styles={{
              ...multiSelectStyles,
              control: (base) => ({
                ...base,
                minHeight: 48,
                borderRadius: 4,
                background: theme.palette.background.paper,
              }),
              menuPortal: (base) => ({
                ...base,
                zIndex: 2000,
              }),
            }}
            menuPortalTarget={document.body}
            menuPosition="fixed"
            placeholder={
              t(`${translationPrefix}.userGroupsPlaceholder`) ||
              t(`${translationPrefix}.authorisedUserGroupsPlaceholder`) ||
              "Select user groups..."
            }
            aria-label={t(`${translationPrefix}.authorisedUserGroupsAria`)}
          />
          {touched.userGroups && !isUserGroupValid && (
            <Typography
              color="error"
              variant="body2"
              sx={{
                mt: 1,
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              ⚠️ {t(`${translationPrefix}.errorNoUserGroup`)}
            </Typography>
          )}
        </Grid>

        <Grid size={12}>
          <Box
            sx={{
              p: 2,
              bgcolor:
                theme.palette.mode === "dark" ? "grey.800" : "background.paper",
              borderRadius: 1,
              border: 1,
              borderColor: "divider",
            }}
          >
            <Typography
              variant="subtitle1"
              gutterBottom
              sx={{ fontWeight: "medium", mb: 1 }}
            >
              {t(`${translationPrefix}.additionalSettingsTitle`)}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {t(`${translationPrefix}.selfActionableDescription`)}
            </Typography>
            <FormControlLabel
              control={
                <Checkbox
                  checked={nodeData.self_actionable || false}
                  onChange={(e) =>
                    setNodeData({
                      ...nodeData,
                      self_actionable: e.target.checked ? true : false,
                    })
                  }
                />
              }
              label={t(`${translationPrefix}.selfActionableLabel`)}
            />
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default StepBasicInfo;
